import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ButtonSpinner, Loader } from '../../ui/Loader';
import {
  useGetServerOptionsQuery,
  useGetOrderSummaryMutation,
  usePurchaseProxyMutation,
} from '../../../store/api/proxiesApi';
import { ProxyOrderSummaryData, Server1Options } from '../../../types/api';

const SERVER_ID = 1;

export function Server1Wizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<'service' | 'config' | 'review'>('service');
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [countrySearch, setCountrySearch] = useState<string>('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedIsp, setSelectedIsp] = useState<string>('');
  const [periodValue, setPeriodValue] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProxyOrderSummaryData | null>(null);
  const quantity = 1;
  const autoExtend = false;

  const { data: optionsData, isLoading: loadingOptions } = useGetServerOptionsQuery(SERVER_ID);
  const [purchaseProxy, { isLoading: isPurchasing }] = usePurchaseProxyMutation();
  const [getOrderSummary, { isLoading: loadingSummary }] = useGetOrderSummaryMutation();

  const options = optionsData as Server1Options | undefined;
  const optionsPayload = (options as { data?: Server1Options })?.data ?? options;
  const services = optionsPayload?.options?.services ?? [];
  const allCountries = optionsPayload?.options?.countries ?? [];
  const countries = countrySearch.trim()
    ? allCountries.filter(
        (c) =>
          c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          c.code.toLowerCase().includes(countrySearch.toLowerCase())
      )
    : allCountries;
  const ispsMap = optionsPayload?.options?.isps ?? {};
  const periods = optionsPayload?.options?.periods ?? { months: [], days: [] };
  const selectedService = services.find((s) => s.id === selectedServiceId);
  const plans = selectedService?.plans ?? [];
  /** ISPs for the selected country code only (API: `options.isps[countryCode]`). */
  const ispsForCountry = selectedCountry ? ispsMap[selectedCountry] ?? [] : [];
  const showIspSection = Boolean(selectedCountry);

  // Close country dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target as Node)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleServiceSelect = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    setStep('config');
    setError(null);
  };

  const handleBack = () => {
    if (step === 'config') {
      setStep('service');
      setSelectedServiceId('');
      setSelectedPlanId('');
      setSelectedCountry('');
      setCountrySearch('');
      setSelectedIsp('');
    } else if (step === 'review') {
      setStep('config');
      setSummary(null);
    }
  };

  const handleNext = () => {
    setError(null);
    if (step === 'config') {
      if (!selectedPlanId || !selectedCountry || !periodValue) {
        setError(t('proxies.wizard.errors.requiredFields'));
        return;
      }
      if (selectedCountry && ispsForCountry.length > 0 && !selectedIsp) {
        setError(t('proxies.wizard.errors.ispRequired'));
        return;
      }
      setSummary(null);
      setStep('review');
    }
  };

  // Fetch order summary when on review step
  useEffect(() => {
    if (step !== 'review' || !selectedServiceId || !selectedPlanId || !selectedCountry) return;
    setError(null);
    getOrderSummary({
      serverId: 1,
      body: {
        serviceId: selectedServiceId,
        orderConfig: {
          planId: selectedPlanId,
          country: selectedCountry,
          period: { unit: 'month', value: periodValue },
          quantity,
          autoExtend: { isEnabled: autoExtend },
          ...(selectedIsp ? { isp: selectedIsp } : {}),
        },
      },
    })
      .unwrap()
      .then((res) => setSummary(res.data))
      .catch((err: any) => {
        const msg = err?.data?.message || err?.message || t('proxies.purchase.error');
        setError(msg);
        setSummary(null);
      });
  }, [step, selectedServiceId, selectedPlanId, selectedCountry, periodValue, selectedIsp]);

  const handlePurchase = async () => {
    setError(null);
    try {
      const response = await purchaseProxy({
        serverId: 1,
        serviceId: selectedServiceId,
        orderConfig: {
          planId: selectedPlanId,
          country: selectedCountry,
          period: { unit: 'month', value: periodValue },
          quantity,
          autoExtend: { isEnabled: autoExtend },
          ...(selectedIsp ? { isp: selectedIsp } : {}),
        },
      }).unwrap();

      toast.success(t('proxies.purchase.success'));
      if (response.data.proxies && response.data.proxies.length > 0 && response.data.proxies[0]) {
        navigate(`/proxies/${response.data.proxies[0]._id}`);
      } else {
        navigate('/proxies/list');
      }
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || t('proxies.purchase.error');
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div
            className={`flex items-center ${step === 'service' ? 'text-brand-accent' : 'text-neutral-medium'}`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step === 'service'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-medium'
              }`}
            >
              1
            </div>
          </div>
          <div className="flex-1 h-0.5 bg-neutral-dark mx-4" />
          <div
            className={`flex items-center ${step === 'config' ? 'text-brand-accent' : 'text-neutral-medium'}`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step === 'config'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-medium'
              }`}
            >
              2
            </div>
          </div>
          <div className="flex-1 h-0.5 bg-neutral-dark mx-4" />
          <div
            className={`flex items-center ${step === 'review' ? 'text-brand-accent' : 'text-neutral-medium'}`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                step === 'review'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-medium'
              }`}
            >
              3
            </div>
          </div>
        </div>
      </div>

      {/* Step 1: Select Service */}
      {step === 'service' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server1.selectService')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((service) => (
              <button
                key={service.id}
                onClick={() => handleServiceSelect(service.id)}
                className="p-4 bg-neutral-dark rounded-lg border border-neutral-dark hover:border-brand-accent/50 transition-all text-left"
              >
                <h3 className="font-heading font-semibold text-neutral-light mb-1">
                  {service.name}
                </h3>
                <p className="text-neutral-medium text-sm font-body">{service.label}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Configure Order */}
      {step === 'config' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server1.configureOrder')}
          </h2>
          <div className="space-y-6">
            {/* Plan (planId) - from selected service's plans */}
            {plans.length > 0 && (
              <div>
                <label className="block text-neutral-light font-body mb-2">
                  {t('proxies.wizard.server1.plan')}
                </label>
                <select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
                >
                  <option value="">{t('proxies.wizard.selectOption')}</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Country - dropdown with search inside list */}
            {allCountries.length > 0 && (
              <div ref={countryDropdownRef} className="relative">
                <label className="block text-neutral-light font-body mb-2">
                  {t('proxies.wizard.server1.country')}
                </label>
                <button
                  type="button"
                  onClick={() => setCountryDropdownOpen((o) => !o)}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-left text-neutral-light font-body flex justify-between items-center"
                >
                  <span className={selectedCountry ? '' : 'text-neutral-medium'}>
                    {selectedCountry
                      ? allCountries.find((c) => c.code === selectedCountry)?.name ?? selectedCountry
                      : t('proxies.wizard.selectOption')}
                  </span>
                  <span className="text-neutral-medium">▾</span>
                </button>
                {countryDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full rounded-lg border border-neutral-dark bg-neutral-dark shadow-xl max-h-72 flex flex-col overflow-hidden">
                    <div className="p-2 border-b border-neutral-dark sticky top-0 bg-neutral-dark">
                      <input
                        type="text"
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        placeholder={t('proxies.wizard.server1.searchCountry')}
                        className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded text-neutral-light font-body text-sm placeholder-neutral-medium"
                        autoFocus
                      />
                    </div>
                    <ul className="overflow-y-auto flex-1 py-1 max-h-56 bg-neutral-dark">
                      {countries.map((country) => (
                        <li key={country.code}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCountry(country.code);
                              setSelectedIsp('');
                              setCountryDropdownOpen(false);
                              setCountrySearch('');
                            }}
                            className={`w-full px-4 py-2 text-left font-body text-sm hover:bg-brand-accent/20 ${
                              selectedCountry === country.code ? 'bg-brand-accent/30 text-brand-accent' : 'text-neutral-light'
                            }`}
                          >
                            {country.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* ISP: hidden until a country is chosen; list = options.isps[selectedCountry] */}
            {showIspSection && (
              <div>
                <label className="block text-neutral-light font-body mb-2">
                  {t('proxies.wizard.server1.isp')}
                  {ispsForCountry.length > 0 ? (
                    <span className="text-brand-accent ml-1" aria-hidden>
                      *
                    </span>
                  ) : (
                    <span className="text-neutral-medium text-sm font-normal ml-1">
                      ({t('proxies.wizard.optional')})
                    </span>
                  )}
                </label>
                {ispsForCountry.length > 0 ? (
                  <select
                    value={selectedIsp}
                    onChange={(e) => setSelectedIsp(e.target.value)}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
                    required
                  >
                    <option value="">{t('proxies.wizard.selectOption')}</option>
                    {ispsForCountry.map((isp) => (
                      <option key={isp.id} value={isp.id}>
                        {isp.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-neutral-medium font-body px-1 py-2 rounded-lg border border-neutral-dark/60 bg-neutral-dark/30">
                    {t('proxies.wizard.server1.noIspsForCountry')}
                  </p>
                )}
              </div>
            )}

            {/* Period (months only) - fixed unit */}
            <div>
              <label className="block text-neutral-light font-body mb-2">
                {t('proxies.wizard.server1.period')} ({t('proxies.wizard.server1.months')})
              </label>
              {periods.months?.length ? (
                <select
                  value={periodValue}
                  onChange={(e) => setPeriodValue(Number(e.target.value))}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
                >
                  {periods.months.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="number"
                  value={periodValue}
                  onChange={(e) => setPeriodValue(Number(e.target.value) || 1)}
                  min={1}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
                />
              )}
            </div>

            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 font-body">
                {error}
              </div>
            )}

            <div className="flex gap-4">
              <button
                onClick={handleBack}
                className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
              >
                {t('common.back')}
              </button>
              <button
                onClick={handleNext}
                className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all font-body font-semibold"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 'review' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.review')}
          </h2>
          <div className="space-y-4 mb-6">
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server1.service')}:</span>
              <span className="text-neutral-light font-body">
                {services.find((s) => s.id === selectedServiceId)?.name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server1.plan')}:</span>
              <span className="text-neutral-light font-body">
                {plans.find((p) => p.id === selectedPlanId)?.name ?? selectedPlanId}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server1.country')}:</span>
              <span className="text-neutral-light font-body">
                {allCountries.find((c) => c.code === selectedCountry)?.name ?? selectedCountry}
              </span>
            </div>
            {selectedIsp && (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server1.isp')}:</span>
                <span className="text-neutral-light font-body">
                  {ispsForCountry.find((i) => i.id === selectedIsp)?.name ?? selectedIsp}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server1.period')}:</span>
              <span className="text-neutral-light font-body">
                {periodValue} {periodValue === 1 ? t('proxies.wizard.server1.month') : t('proxies.wizard.server1.months')}
              </span>
            </div>
            {loadingSummary && (
              <div className="flex items-center justify-center py-2">
                <ButtonSpinner size="sm" className="text-brand-accent" />
              </div>
            )}
            {summary && !loadingSummary && (
              <div className="pt-4 mt-4 border-t border-neutral-dark space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-medium font-body">
                    {t('proxies.wizard.server1.finalPrice')}
                    {summary.isEstimate && (
                      <span className="ml-2 text-sm text-amber-400">
                        ({t('proxies.wizard.server1.priceEstimate')})
                      </span>
                    )}
                  </span>
                  <span className="text-neutral-light font-body font-semibold">
                    {(() => {
                      const finalPrice = Number(summary.finalPrice);
                      return Number.isFinite(finalPrice)
                        ? finalPrice.toFixed(2)
                        : String(summary.finalPrice);
                    })()} {summary.currency}
                  </span>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 font-body mb-6">
              {error}
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={handleBack}
              disabled={isPurchasing}
              className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body disabled:opacity-50"
            >
              {t('common.back')}
            </button>
            <button
              onClick={handlePurchase}
              disabled={isPurchasing || loadingSummary}
              className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all font-body font-semibold disabled:opacity-50"
            >
              {isPurchasing ? t('common.processing') : t('proxies.purchase.cta')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

